function bmiCalculator() {
    return {
        units: 'metric',
        heightCm: '',
        heightFt: '',
        heightIn: '',
        weight: '',
        age: '',
        gender: '',
        
        bmi: 0,
        category: '-',
        statusColor: '#fff',
        gaugePercent: 0,
        
        idealWeightRange: '-',
        weightInsightLabel: 'Difference',
        weightInsightValue: '-',
        
        bodyFatPercent: null,
        bodyFatCategory: '-',
        bodyFatColor: '#fff',
        ageGuidance: '',
        
        history: [],

        init() {
            const saved = localStorage.getItem('bmi_history');
            if (saved) this.history = JSON.parse(saved);
        },

        calculate() {
            let hMeters = 0;
            let wKg = 0;

            if (this.units === 'metric') {
                if (this.heightCm > 0) hMeters = this.heightCm / 100;
                wKg = parseFloat(this.weight) || 0;
            } else {
                const totalInches = (parseFloat(this.heightFt) || 0) * 12 + (parseFloat(this.heightIn) || 0);
                if (totalInches > 0) hMeters = totalInches * 0.0254;
                wKg = (parseFloat(this.weight) || 0) * 0.453592;
            }

            if (hMeters > 0 && wKg > 0) {
                const score = wKg / (hMeters * hMeters);
                this.bmi = score.toFixed(1);
                this.classify(score);
                this.getInsights(hMeters, wKg);
                this.estimateBodyFat(score);
                this.getAgeSpecificGuidance();
            } else {
                this.bmi = 0;
                this.category = '-';
                this.gaugePercent = 0;
                this.bodyFatPercent = null;
                this.bodyFatCategory = '-';
                this.ageGuidance = '';
            }
        },

        classify(score) {
            // Gauge 0% = BMI 15, 100% = BMI 40
            let percent = ((score - 15) / (40 - 15)) * 100;
            this.gaugePercent = Math.min(Math.max(percent, 0), 100);

            if (score < 18.5) {
                this.category = 'Underweight';
                this.statusColor = '#3b82f6';
            } else if (score < 25) {
                this.category = 'Normal Weight';
                this.statusColor = '#10b981';
            } else if (score < 30) {
                this.category = 'Overweight';
                this.statusColor = '#f59e0b';
            } else if (score < 35) {
                this.category = 'Obese Class I';
                this.statusColor = '#ef4444';
            } else {
                this.category = 'Obese Class II';
                this.statusColor = '#991b1b';
            }
        },

        getInsights(h, w) {
            // Healthy BMI range: 18.5 to 24.9
            const minW = 18.5 * (h * h);
            const maxW = 24.9 * (h * h);

            if (this.units === 'metric') {
                this.idealWeightRange = `${minW.toFixed(1)}kg – ${maxW.toFixed(1)}kg`;
                if (w < minW) {
                    this.weightInsightLabel = 'To Gain';
                    this.weightInsightValue = `${(minW - w).toFixed(1)} kg`;
                } else if (w > maxW) {
                    this.weightInsightLabel = 'To Lose';
                    this.weightInsightValue = `${(w - maxW).toFixed(1)} kg`;
                } else {
                    this.weightInsightLabel = 'Status';
                    this.weightInsightValue = 'Healthy Range';
                }
            } else {
                const minLb = minW * 2.20462;
                const maxLb = maxW * 2.20462;
                this.idealWeightRange = `${minLb.toFixed(1)}lb – ${maxLb.toFixed(1)}lb`;
                const wLb = w * 2.20462;
                if (wLb < minLb) {
                    this.weightInsightLabel = 'To Gain';
                    this.weightInsightValue = `${(minLb - wLb).toFixed(1)} lb`;
                } else if (wLb > maxLb) {
                    this.weightInsightLabel = 'To Lose';
                    this.weightInsightValue = `${(wLb - maxLb).toFixed(1)} lb`;
                } else {
                    this.weightInsightLabel = 'Status';
                    this.weightInsightValue = 'Healthy Range';
                }
            }
        },

        estimateBodyFat(bmiScore) {
            // Only calculate if both age and gender are provided
            if (!this.age || !this.gender) {
                this.bodyFatPercent = null;
                this.bodyFatCategory = '-';
                this.bodyFatColor = '#fff';
                return;
            }

            // Deurenberg formula for body fat percentage
            // Men: BF% = 1.20 × BMI + 0.23 × Age - 16.2
            // Women: BF% = 1.20 × BMI + 0.23 × Age - 5.4
            let bodyFat;
            if (this.gender === 'male') {
                bodyFat = (1.20 * bmiScore) + (0.23 * this.age) - 16.2;
            } else {
                bodyFat = (1.20 * bmiScore) + (0.23 * this.age) - 5.4;
            }

            this.bodyFatPercent = Math.max(0, bodyFat).toFixed(1);
            this.classifyBodyFat(bodyFat);
        },

        classifyBodyFat(bodyFat) {
            // Body fat categories differ by gender
            const categories = this.gender === 'male' ? {
                essential: 5,
                athletes: 13,
                fitness: 17,
                average: 24,
                obese: 25
            } : {
                essential: 14,
                athletes: 20,
                fitness: 24,
                average: 31,
                obese: 32
            };

            if (bodyFat < categories.essential) {
                this.bodyFatCategory = 'Essential Fat';
                this.bodyFatColor = '#3b82f6';
            } else if (bodyFat < categories.athletes) {
                this.bodyFatCategory = 'Athletes';
                this.bodyFatColor = '#10b981';
            } else if (bodyFat < categories.fitness) {
                this.bodyFatCategory = 'Fitness';
                this.bodyFatColor = '#10b981';
            } else if (bodyFat < categories.average) {
                this.bodyFatCategory = 'Average';
                this.bodyFatColor = '#f59e0b';
            } else {
                this.bodyFatCategory = 'Obese';
                this.bodyFatColor = '#ef4444';
            }
        },

        getAgeSpecificGuidance() {
            if (!this.age) {
                this.ageGuidance = '';
                return;
            }

            if (this.age < 20) {
                this.ageGuidance = 'Note: For individuals under 20, BMI should be evaluated using age and sex-specific percentile charts.';
            } else if (this.age >= 65) {
                this.ageGuidance = 'For adults 65+, a BMI range of 23-27 may be associated with better health outcomes.';
            } else {
                this.ageGuidance = '';
            }
        },

        saveRecord() {
            if (this.bmi == 0) return;
            const record = {
                bmi: this.bmi,
                category: this.category,
                weight: this.weight,
                unit: this.units === 'metric' ? 'kg' : 'lb',
                color: this.statusColor,
                date: new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
            };
            this.history.unshift(record);
            if (this.history.length > 5) this.history.pop();
            localStorage.setItem('bmi_history', JSON.stringify(this.history));
        },

        clearHistory() {
            this.history = [];
            localStorage.removeItem('bmi_history');
        },

        reset() {
            this.weight = ''; this.heightCm = ''; this.heightFt = ''; this.heightIn = '';
            this.age = ''; this.gender = ''; this.bmi = 0;
        }
    };
}

if (typeof window.Alpine !== 'undefined') {
    Alpine.data('bmiCalculator', bmiCalculator);
} else {
    document.addEventListener('alpine:init', () => {
        Alpine.data('bmiCalculator', bmiCalculator);
    });
}
